// frontend/src/components/conversion/conversionPopupPages.js
//
// WHERE the conversion popup may appear, and what `page_type` each place
// reports. An explicit ALLOWLIST, deliberately not "every public page": the
// legal pages, /supporter-network, the auth/token pages and the signed-in app
// never show it, and a new public route stays popup-free until it is added
// here on purpose.
//
// EAGER (imported by ConversionPopupGate, which every public AuthLayout
// mounts), so it stays tiny: the profession list comes from the registry,
// which already rides in the eager bundle (App.js and HomeNav read it) and
// holds slugs and routes only - never a content module. The blog slugs come
// from content/blog/blogIndex.js - the LIGHT metadata list the homepage's
// blog strip already pulls into the eager bundle - never from blogData.js,
// which carries every article's prose.

import professionRegistry from "../../content/professions/registry";
import { articleIndex } from "../../content/blog/blogIndex";

/** `page_type` values - mirror services/analytics.js CONVERSION_POPUP_PARAM_SPEC. */
export const PAGE_TYPE_HOME = "home";
export const PAGE_TYPE_PROFESSION = "profession";
export const PAGE_TYPE_BLOG = "blog";
export const PAGE_TYPE_LANDING = "landing";

/** The feature landings that carry the popup. Not /supporter-network. */
const LANDING_ROUTES = new Set(["/business-website", "/facebook-group-scanning"]);

/** Profession route -> slug, built once from the registry. */
const PROFESSION_SLUG_BY_ROUTE = new Map(
  professionRegistry.PROFESSION_PAGES.map(({ slug, route }) => [route, slug])
);

/**
 * The real articles. `/blog/:slug` matches ANY slug, so an unknown one (a
 * typo, a removed article) renders the not-found view - no place for the popup.
 */
const BLOG_ARTICLE_SLUGS = new Set(articleIndex.map(({ slug }) => slug));
const BLOG_PREFIX = "/blog/";

/**
 * Drop one trailing slash (except on "/") so "/blog/" and "/blog" agree.
 *
 * @param {string} pathname
 * @returns {string}
 */
function normalizePath(pathname) {
  if (typeof pathname !== "string" || !pathname) return "";
  return pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

/**
 * Is `path` one real article's page - exactly one segment under /blog that is
 * a published slug?
 *
 * @param {string} path - normalized pathname
 * @returns {boolean}
 */
function isKnownBlogArticle(path) {
  if (!path.startsWith(BLOG_PREFIX)) return false;
  return BLOG_ARTICLE_SLUGS.has(path.slice(BLOG_PREFIX.length));
}

/**
 * The popup's view of a route.
 *
 * @param {string} pathname - the router's pathname (no query, no hash)
 * @returns {{pageType: string, professionSlug: string|null}|null} null when the
 *   popup must not appear on this route.
 */
export function resolvePopupPage(pathname) {
  const path = normalizePath(pathname);
  if (!path) return null;
  if (path === "/") return { pageType: PAGE_TYPE_HOME, professionSlug: null };
  const slug = PROFESSION_SLUG_BY_ROUTE.get(path);
  if (slug) return { pageType: PAGE_TYPE_PROFESSION, professionSlug: slug };
  if (path === "/blog" || isKnownBlogArticle(path)) {
    return { pageType: PAGE_TYPE_BLOG, professionSlug: null };
  }
  if (LANDING_ROUTES.has(path)) return { pageType: PAGE_TYPE_LANDING, professionSlug: null };
  return null;
}
